import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import PxGrid from 'components/layout/PxGrid';
import { FormApi, FormState } from 'final-form';
import moment from 'moment';
import React, { useRef } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Button, Grid, Header } from 'semantic-ui-react';
import axios from 'services/http';
import {
  detailedExportInvoiceStatistic2,
  exportInvoiceStatistic2,
  getInvoiceStatisticOnlyByTreatmentDate
} from 'services/invoiceStatisticServices';
import styled from 'styled-components';
import { InvoiceStatistic2Request, InvoiceStatisticGroupData, InvoiceStatisticRequest } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;

  .search-form {
    max-width: 960px;
    display: flex;
    flex-direction: row;

    .left-panel, .right-panel {
      flex: 1 1 auto;

      .header {
        font-weight: bold;
      }

      .group-by-period .checkbox {
        margin-right: 1rem;
      }
    }

    .ui.grid > * {
      padding-top: 0.1rem !important;
      padding-bottom: 0.5rem !important;
    }
  }

  .ui.buttons {
    margin-left: 10px !important;
  }

  .field {
    display: inline-block !important;
    min-width: 12rem;
  }
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  .ui.header.tiny {
    font-size: 1em !important;
  }
`;

const SearchOptionsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchButtonContainer = styled.div`
  flex: 1 1 100%;
  margin-top: 1rem;
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  onFilterInvoiceStatisticData: (invoiceStatisticData: InvoiceStatisticGroupData) => void,
  onInvoiceStatisticLoadingChange: (dataLoading: boolean) => void,
  onChangeGroupByInterrupted: (groupByInterrupted: boolean) => void,
  setFormDataValues: (formData: InvoiceStatisticRequest) => void,
  setErrorMessage: (errorMessage?: string) => void
}

const InvoiceStatistic2OptionsSegment = (props: Props) => {

  const {
    onInvoiceStatisticLoadingChange,
    onFilterInvoiceStatisticData,
    onChangeGroupByInterrupted,
    setFormDataValues,
    setErrorMessage,
    currentUser,
    getMessage
  } = props;

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  const initialSearchValues = useRef<Partial<Partial<InvoiceStatistic2Request>>>();
  if (!initialSearchValues.current) {
    const fromDate = moment().startOf('year').format("YYYY-MM-DD");
    const toDate = moment().endOf('month').format("YYYY-MM-DD");

    // const fromDate = "2019-01-01";
    // const toDate = "2020-12-31";

    initialSearchValues.current = {
      mandateId: currentUser ? currentUser.userConfig.mandateId : undefined,
      fromDate: fromDate,
      toDate: toDate,
      includeInvoicesAndPaymentOutsideTimeRange: true,
      detailedSearch: true,
      groupByYear: false,
      groupByQuarter: true,
      groupByTariff: false,
      groupByInterrupted: false
    }
  }

  const handleChange = ({values}: FormState, form: FormApi) => {
    //const toDate = values.toDate && new Date(values.toDate);
  };

  const fetchInvoiceStatisticData = (values: any, exportToFile?: boolean, detailedExport?: boolean) => {

    onInvoiceStatisticLoadingChange(true);

    let request = {
      ...values
    };
    if (!values.detailedSearch) {
      request.fromDate = undefined;
      request.includeInvoicesAndPaymentOutsideTimeRange = false;
      request.groupByYear = true;
      request.groupByQuarter = false;
      request.groupByMonth = false;
      request.groupByTariff = true;
      request.groupByInterrupted = true;
    }
    if (exportToFile && !detailedExport) {
      exportInvoiceStatistic2(request, cancelTokenSource)
          .catch(noop)
          .finally(() => {
            onInvoiceStatisticLoadingChange(false);
          })
    } else if (exportToFile && detailedExport) {
      detailedExportInvoiceStatistic2(request, cancelTokenSource)
          .catch(noop)
          .finally(() => {
            onInvoiceStatisticLoadingChange(false);
          })
    } else {
      onChangeGroupByInterrupted(values.groupByInterrupted);

      getInvoiceStatisticOnlyByTreatmentDate(request, cancelTokenSource)
          .then(response => {
            onFilterInvoiceStatisticData(response);
            setFormDataValues(request);
          })
          .catch((error) => {
            setErrorMessage(getMessage("error.general"));
          })
          .finally(() => {
            onInvoiceStatisticLoadingChange(false);
          });
    }

  };

  const onEnterKeyPressed = (e: any, values: any) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      fetchInvoiceStatisticData(values)
    }
  };

  const renderForm = ({handleSubmit, values, form, submitting}: FormRenderProps): React.ReactNode => {

    const isDetailedSearch = values.detailedSearch;

    return (
        <StyledForm onSubmit={handleSubmit}>
          <StyledContainer>
            <Header>{getMessage("searchOptions")}</Header>
          </StyledContainer>
          <div className="search-form">
            <PxGrid className="left-panel">
              <Grid.Row>
                <Grid.Column width={8}>
                  <Field
                      name="detailedSearch"
                      component={CheckBox}
                      label={getMessage("invoiceStatistic2.searchSection.detailedSearch.checkbox")}
                  />
                </Grid.Column>
              </Grid.Row>
              {isDetailedSearch &&
                  <Grid.Row>
                    <Grid.Column width={4}>
                      {getMessage("from")}:
                    </Grid.Column>
                    <Grid.Column width={12}>
                      <Field
                          name="fromDate"
                          component={DatePicker}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
              }
              <Grid.Row>
                <Grid.Column width={4}>
                  {getMessage("to")}:
                </Grid.Column>
                <Grid.Column width={12}>
                  <Field
                      name="toDate"
                      component={DatePicker}
                      fluid
                  />
                </Grid.Column>
              </Grid.Row>
              {
                  isDetailedSearch &&
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Field
                          name="includeInvoicesAndPaymentOutsideTimeRange"
                          component={CheckBox}
                          label={getMessage("invoiceStatistic2.searchSection.includeInvoicesAndPaymentOutsideTimeRange")}
                      />
                    </Grid.Column>
                  </Grid.Row>
              }
            </PxGrid>

            <PxGrid className="right-panel">
              {
                  isDetailedSearch &&
                  <>
                    <Grid.Row>
                      <Grid.Column width={8}>
                        <span className="header">
                          {getMessage("invoiceStatistic2.searchSection.groupBy")}
                        </span>
                      </Grid.Column>
                    </Grid.Row>

                    <Grid.Row>
                      <Grid.Column className="group-by-period" width={16}>
                        <Field
                            name="groupByYear"
                            component={CheckBox}
                            label={getMessage("invoiceStatistic2.searchSection.groupBy.period-year")}
                        />

                        <Field
                            name="groupByQuarter"
                            component={CheckBox}
                            label={getMessage("invoiceStatistic2.searchSection.groupBy.period-quarter")}
                        />

                        <Field
                            name="groupByMonth"
                            component={CheckBox}
                            label={getMessage("invoiceStatistic2.searchSection.groupBy.period-month")}
                        />
                      </Grid.Column>
                    </Grid.Row>

                    <Grid.Row>
                      <Grid.Column width={8}>
                        <Field
                            name="groupByTariff"
                            component={CheckBox}
                            label={getMessage("invoiceStatistic2.searchSection.groupBy.tariff")}
                        />
                      </Grid.Column>
                    </Grid.Row>

                    <Grid.Row>
                      <Grid.Column width={8}>
                        <Field
                            name="groupByInterrupted"
                            component={CheckBox}
                            label={getMessage("invoiceStatistic2.searchSection.groupBy.interrupted")}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </>
              }
            </PxGrid>
          </div>

          <StyledContainer onKeyDown={(e) => onEnterKeyPressed(e, values)}>
            <SearchOptionsContainer>

              <SearchButtonContainer>
                <Button
                    type="button" primary compact
                    onClick={() => fetchInvoiceStatisticData(values)}
                >
                  {getMessage("search")}
                </Button>
                <Button
                    type="button"
                    compact
                    disabled={submitting}
                    onClick={() => fetchInvoiceStatisticData(values, true)}
                >
                  {getMessage("interventionAvailability.export")}
                </Button>
                <Button
                    type="button"
                    compact
                    disabled={submitting}
                    onClick={() => fetchInvoiceStatisticData(values, true, true)}
                >
                  {getMessage("interventionAvailability.detailedExport")}
                </Button>
              </SearchButtonContainer>
            </SearchOptionsContainer>
          </StyledContainer>
          <FormSpy subscription={{values: true}} onChange={formState => handleChange(formState, form)}/>
        </StyledForm>
    )
  };

  return (
      <FinalForm
          onSubmit={() => noop}
          initialValues={initialSearchValues.current}
          subscription={{values: true, pristine: true}}
          render={renderForm}
      />
  );

}

export default withAuthContext(withMessages(InvoiceStatistic2OptionsSegment));
